import { COVER_IMAGE_ID } from '@/shared/constants/fund-documents';
import { StepForms } from '@/stores/slices/setup-fund-slice';

import { pdsToBackend } from '../common';
import { FundStructure } from '../constants/fund-structure';
import { Fund, FundPresentation, FundPresentationType } from './type';

const isMasterFeeder = (fs: FundStructure | undefined | null): boolean =>
  fs === FundStructure.MasterFeeder;

const isManagedAccountSma = (fs: FundStructure | undefined | null): boolean =>
  fs === FundStructure.ManagedAccountSma;

const withDefaultPresentationType = (presentation: FundPresentation | null): FundPresentation => ({
  presentationType: presentation?.presentationType ?? FundPresentationType.VideoLink,
  videoUrl: presentation?.videoUrl?.trim() ? presentation.videoUrl : null,
  deckFileId: presentation?.deckFileId ?? null,
});

const normalizeLockUpPeriod = (value: unknown): string[] | null => {
  if (Array.isArray(value)) {
    const filtered = value.filter((v): v is string => typeof v === 'string' && v.length > 0);
    return filtered.length > 0 ? filtered : null;
  }
  return typeof value === 'string' && value.length > 0 ? [value] : null;
};

/**
 * Picks payload fields from an existing Fund without any transformation.
 * Use this instead of mapToPayload when updating a single field on an
 * already-saved fund to avoid unintended data changes that trigger
 * re-verification.
 */
export function pickFundPayloadFields(fund: Fund) {
  return {
    name: fund.name,
    fundStructure: fund.fundStructure,
    feeders: isMasterFeeder(fund.fundStructure)
      ? (fund.feedersDraft && fund.feedersDraft.length > 0 ? fund.feedersDraft : fund.feeders).map(
          (f) => ({
            id: f.id,
            name: f.name,
            legalStructure: f.legalStructure,
            jurisdictionCountry: f.jurisdictionCountry,
            jurisdictionState: f.jurisdictionState,
            assignedCountries: f.assignedCountries,
            offeringDocuments: f.offeringDocuments,
            subscriptionDocuments: f.subscriptionDocuments,
            riskDisclosureDocuments: f.riskDisclosureDocuments,
            sharesStructure: f.sharesStructure,
            subscriptionOptions: f.subscriptionOptions,
            investorEligibility: f.investorEligibility,
            investmentCompaniesActStatus: f.investmentCompaniesActStatus ?? null,
            previousYearsAudits: f.previousYearsAudits ?? null,
          }),
        )
      : null,
    investmentDescription: fund.investmentDescription,
    participants: fund.participants,
    returnProfile: fund.returnProfile,
    feesData: fund.feesData,
    minimumInvestment: fund.minimumInvestment,
    redemption: fund.redemption,
    duration: fund.duration,
    distributionPeriod: fund.distributionPeriod,
    reportingPeriod: fund.reportingPeriod,
    investorEligibility: fund.investorEligibility,
    countrySpecificExemptions: fund.countrySpecificExemptions,
    offeringDocuments: fund.offeringDocuments,
    subscriptionDocuments: fund.subscriptionDocuments,
    riskDisclosureDocuments: fund.riskDisclosureDocuments,
    marketingDocuments: fund.marketingDocuments,
    previousYearsAudits: isMasterFeeder(fund.fundStructure) ? [] : fund.previousYearsAudits,
    coverImageId: fund.coverImageId || COVER_IMAGE_ID,
    cardImageId: fund.cardImageId || COVER_IMAGE_ID,
    logoId: fund.logoId,
    investmentAvailabilityStatus: fund.investmentAvailabilityStatus,
    transferability: fund.transferability,
    sharesStructure: fund.sharesStructure,
    subscriptionOptions: fund.subscriptionOptions,
    accountingType: fund.accountingType,
    investmentCompaniesActStatus: isMasterFeeder(fund.fundStructure)
      ? null
      : (fund.investmentCompaniesActStatus ?? null),
    howFundWorks: fund.howFundWorks,
    fundBenefits: fund.fundBenefits,
    fundPresentation: withDefaultPresentationType(fund.fundPresentation),
    fundTargetInvestor: fund.fundTargetInvestor,
    managerAlignment: fund.managerAlignment,
    faq: fund.faq,
  };
}

export function mapToPayload(fund: StepForms | Fund) {
  const isStepForms = 'minInvestmentAmounts' in fund || 'numberUnits' in fund;

  const fundStructure: FundStructure = isStepForms
    ? ((fund as StepForms).fundStructure ?? FundStructure.Standard)
    : ((fund as Fund).fundStructure ?? FundStructure.Standard);
  const masterFeeder = isMasterFeeder(fundStructure);
  const managedAccountSma = isManagedAccountSma(fundStructure);

  const feedersPayload =
    isStepForms && masterFeeder
      ? (fund as StepForms).feeders.map((f) => ({
          ...(f.id ? { id: f.id } : {}),
          name: f.name ?? '',
          legalStructure: f.legalStructure ?? '',
          jurisdictionCountry: f.jurisdictionCountry ?? '',
          jurisdictionState: f.jurisdictionState ?? null,
          assignedCountries: f.assignedCountries,
          offeringDocuments: f.offeringDocuments,
          subscriptionDocuments: f.subscriptionDocuments,
          riskDisclosureDocuments: f.riskDisclosure,
          sharesStructure: {
            shares: (f.numberUnits.shareClasses ?? []).map((sc) => ({
              type: sc.className,
              numberOfShares: sc.shares != null ? Number(sc.shares) : null,
              shareholders: sc.shareholders.map((sh) => ({
                type: sh.type ?? null,
                fullName: sh.fullName ?? null,
                email: sh.email ?? null,
                numberOfShares: sh.numberOfShares != null ? Number(sh.numberOfShares) : null,
                percentage: sh.percentage != null ? Number(sh.percentage) : null,
              })),
            })),
          },
          subscriptionOptions:
            f.subscriptionOptions?.subscriptionMethod === 'Crypto'
              ? { subscriptionMethod: 'Crypto', wireInformation: null }
              : f.subscriptionOptions,
          investorEligibility: {
            allowed:
              f.investorEligibility.allowed.length > 0 ? f.investorEligibility.allowed : null,
            partiallyRestricted:
              f.investorEligibility.partiallyRestricted.length > 0
                ? f.investorEligibility.partiallyRestricted
                : null,
          },
          investmentCompaniesActStatus: f.investmentCompaniesActStatus ?? null,
          previousYearsAudits: (f.previousYearsAudits ?? []).map((a) => ({
            documentId: a.documentId,
            period: a.period || null,
            notes: a.notes || null,
          })),
        }))
      : null;

  const name = isStepForms ? (fund.investmentDescription?.name ?? null) : (fund.name ?? null);
  const investmentDescription = {
    description: fund.investmentDescription.description || null,
    category: fund.investmentDescription.category || null,
    website: fund.investmentDescription.website || null,
    assets: fund.investmentDescription.assets || null,
    stage: Array.isArray(fund.investmentDescription.stage)
      ? fund.investmentDescription.stage
      : fund.investmentDescription.stage
        ? [fund.investmentDescription.stage as unknown as string]
        : null,
    strategies: fund.investmentDescription.strategies || null,
    sizeInMillionsUsd:
      fund.investmentDescription.sizeInMillionsUsd !== '' &&
      fund.investmentDescription.sizeInMillionsUsd != null
        ? Number(fund.investmentDescription.sizeInMillionsUsd)
        : null,
    aumInMillionsUsd:
      fund.investmentDescription.aumInMillionsUsd !== '' &&
      fund.investmentDescription.aumInMillionsUsd != null
        ? Number(fund.investmentDescription.aumInMillionsUsd)
        : null,
    legalStructure: fund.investmentDescription.legalStructure || null,
    jurisdictionCountry: fund.investmentDescription.jurisdictionCountry || null,
    jurisdictionState: fund.investmentDescription.jurisdictionState || null,
  };
  if (masterFeeder) {
    // Master-Feeder: legal structure + jurisdiction live on each feeder, not on the master.
    investmentDescription.legalStructure = null;
    investmentDescription.jurisdictionCountry = null;
    investmentDescription.jurisdictionState = null;
  }
  const accountingType = fund.accountingType || null;
  const investmentCompaniesActStatus = masterFeeder
    ? null
    : (fund.investmentCompaniesActStatus ?? null);

  const participants = (fund.participants || [])
    .filter((m) => Boolean(String(m?.name ?? '').trim()))
    .map((m) => ({
      name: m?.name || null,
      role: m?.role || null,
      logoImageId: m?.logoImageId || null,
      website: m?.website || null,
    }));

  const feesData = {
    description: fund.feesData.description || null,
    fees:
      Array.isArray(fund.feesData?.fees) && fund.feesData.fees.length > 0
        ? fund.feesData.fees.map((f) => ({
            feeType: f.feeType,
            percentage: f.percentage ? Number(f.percentage) : null,
            type: f.type,
            notes: f.notes || null,
            frequency: f.frequency || null,
          }))
        : [],
  };

  const minimumInvestment = isStepForms
    ? (() => {
        const minLines = fund.minInvestmentAmounts?.lines || [];
        const primary = minLines[0] || null;
        const secondary = minLines.length > 1 ? minLines[1] : null;
        return {
          currency: fund.minInvestmentAmounts?.currency || null,
          descriptionMinimumInvestment: primary?.description || null,
          descriptionSecondaryMinimumInvestment: secondary?.description || null,
          valueMinimumInvestment: primary && primary.value ? Number(primary.value) : null,
          valueSecondaryInvestment: secondary && secondary.value ? Number(secondary.value) : null,
          notesMinimumInvestment: primary?.notes || null,
          notesSecondaryMinimumInvestment: secondary?.notes || null,
          notApplicableMinimumInvestment: primary?.notApplicable || false,
          notApplicableSecondaryMinimumInvestment: secondary?.notApplicable || false,
          additionalDescription: fund.minInvestmentAmounts?.additionalDescription || null,
        };
      })()
    : (() => {
        return {
          currency: fund.minimumInvestment.currency || null,
          descriptionMinimumInvestment: fund.minimumInvestment.descriptionMinimumInvestment || null,
          descriptionSecondaryMinimumInvestment:
            fund.minimumInvestment.descriptionSecondaryMinimumInvestment || null,
          valueMinimumInvestment: fund.minimumInvestment.valueMinimumInvestment
            ? Number(fund.minimumInvestment.valueMinimumInvestment)
            : null,
          valueSecondaryInvestment: fund.minimumInvestment.valueSecondaryInvestment
            ? Number(fund.minimumInvestment.valueSecondaryInvestment)
            : null,
          notesMinimumInvestment: fund.minimumInvestment.notesMinimumInvestment || null,
          notesSecondaryMinimumInvestment:
            fund.minimumInvestment.notesSecondaryMinimumInvestment || null,
          notApplicableMinimumInvestment: !!fund.minimumInvestment.notApplicableMinimumInvestment,
          notApplicableSecondaryMinimumInvestment:
            !!fund.minimumInvestment.notApplicableSecondaryMinimumInvestment,
          additionalDescription: fund.minimumInvestment.additionalDescription || null,
        };
      })();

  const redemption = isStepForms
    ? (() => {
        const r = fund.redemption;
        const rawMax = r?.max;
        const clampedMax = rawMax == null ? null : Math.max(0, Math.min(100, Number(rawMax)));

        return {
          conditions: r?.conditions ?? null,
          lockUpPeriod: normalizeLockUpPeriod(r?.lockUpPeriod),
          frequency: r?.frequency ?? null,
          priority: r?.priority ?? null,
          noticeInDays: r?.noticeInDays ? Number(r.noticeInDays) : null,
          noRedemptionAllowed: !!r?.noRedemptionAllowed,
          gate: r.noRedemptionGates
            ? null
            : {
                max: clampedMax ? Number(clampedMax) : null,
                gates: r?.gates ?? null,
                months: r?.months ? Number(r.months) : null,
              },
        };
      })()
    : (() => {
        const r = fund.redemption;
        const rawMax = r.gate?.max;
        const clampedMax = rawMax == null ? null : Math.max(0, Math.min(100, Number(rawMax)));

        return {
          conditions: r.conditions ?? null,
          lockUpPeriod: normalizeLockUpPeriod(r.lockUpPeriod),
          frequency: r.frequency ?? null,
          priority: r.priority ?? null,
          noticeInDays: r.noticeInDays ?? null,
          noRedemptionAllowed: !!r.noRedemptionAllowed,
          gate:
            r.gate === null
              ? null
              : {
                  max: clampedMax,
                  gates: r.gate?.gates ?? null,
                  months: r.gate?.months ?? null,
                },
        };
      })();

  const duration = {
    startDate: fund.duration.startDate || null,
    isOpenEnded: !!fund.duration.isOpenEnded,
    isNewFund: !!fund.duration.isNewFund,
    durationInYears: fund.duration.durationInYears ? Number(fund.duration.durationInYears) : null,
    additionalInfo: fund.duration.additionalInfo || null,
  };

  const dist = fund.distributionPeriod || {};
  const distributionPeriod = {
    frequency: dist.frequency ? Number(dist.frequency) : null,
    period: dist.period || null,
    frequencyNotApplicable: dist.frequencyNotApplicable || false,
    additionalInfo: dist.additionalInfo || null,
    type: dist.type || null,
  };

  const rep = fund.reportingPeriod || {};
  const reportingPeriod = {
    period: rep.period || null,
    reportingNotApplicable: rep.reportingNotApplicable || false,
    additionalInfo: rep.additionalInfo || null,
  };

  const investorEligibility = {
    filter: fund.investorEligibility.filter || null,
    allowed:
      Array.isArray(fund.investorEligibility.allowed) && fund.investorEligibility.allowed.length
        ? fund.investorEligibility.allowed
        : [],
    notAllowed:
      Array.isArray(fund.investorEligibility.notAllowed) &&
      fund.investorEligibility.notAllowed.length
        ? fund.investorEligibility.notAllowed
        : [],
    partiallyRestricted:
      Array.isArray(fund.investorEligibility.partiallyRestricted) &&
      fund.investorEligibility.partiallyRestricted.length
        ? fund.investorEligibility.partiallyRestricted
        : [],
  };

  const countrySpecificExemptions = isStepForms
    ? (() => {
        const cseRaw = (fund as StepForms).countrySpecificExemptions || {};
        const cseArr = Object.entries(cseRaw).map(([country, exemptions]) => ({
          country,
          exemptions: Array.isArray(exemptions) ? exemptions : [],
        }));
        return cseArr.length > 0 ? cseArr : [];
      })()
    : (() => {
        const arr = Array.isArray((fund as Fund).countrySpecificExemptions)
          ? (fund as Fund).countrySpecificExemptions
          : [];
        return arr.length > 0 ? arr : [];
      })();

  const offeringDocuments = fund.offeringDocuments;
  const subscriptionDocuments = fund.subscriptionDocuments;
  const riskDisclosureDocuments = isStepForms ? fund.riskDisclosure : fund.riskDisclosureDocuments;
  const marketingDocuments = isStepForms ? fund.marketingMaterial : fund.marketingDocuments;

  const previousYearsAudits = (fund.previousYearsAudits ?? []).map((a) => ({
    documentId: a.documentId,
    period: a.period || null,
    notes: a.notes || null,
  }));

  const coverImageId = fund.coverImageId || COVER_IMAGE_ID;
  const cardImageId = fund.cardImageId || COVER_IMAGE_ID;
  const logoId = fund.logoId ?? null;

  const investmentAvailabilityStatus = fund.investmentAvailabilityStatus || null;

  const preferredDataSource = isStepForms
    ? pdsToBackend[(fund as StepForms).returnProfile.navData?.preferredDataSource ?? ''] || 'Merge'
    : (fund as Fund).returnProfile.preferredDataSource || 'Merge';

  const returnProfile = {
    projectedReturnProfile: fund.returnProfile.projectedReturnProfile || null,
    feeType: fund.returnProfile.feeType as unknown as string,
    targetReturnRate:
      fund.returnProfile.targetReturnRate !== '' && fund.returnProfile.targetReturnRate != null
        ? Number(fund.returnProfile.targetReturnRate)
        : null,
    strikeFrequency: fund.returnProfile.strikeFrequency || null,
    preferredDataSource,
  };

  const sharesStructure = isStepForms
    ? {
        shares:
          Array.isArray(fund.numberUnits?.shareClasses) && fund.numberUnits.shareClasses.length > 0
            ? fund.numberUnits.shareClasses.map((sc) => ({
                type: sc.className,
                numberOfShares: sc.shares ? Number(sc.shares) : null,
                shareholders: sc.shareholders.map((sh) => ({
                  type: sh.type || null,
                  fullName: sh.fullName || null,
                  email: sh.email || null,
                  numberOfShares: sh.numberOfShares ? Number(sh.numberOfShares) : null,
                  percentage: sh.percentage ? Number(sh.percentage) : null,
                })),
              }))
            : null,
      }
    : {
        shares:
          Array.isArray(fund.sharesStructure?.shares) && fund.sharesStructure?.shares?.length > 0
            ? fund.sharesStructure!.shares.map((sc) => ({
                type: sc.type,
                numberOfShares: sc.numberOfShares != null ? Number(sc.numberOfShares) : null,
                shareholders: sc.shareholders.map((sh) => ({
                  type: sh.type || null,
                  fullName: sh.fullName || null,
                  email: sh.email || null,
                  numberOfShares: sh.numberOfShares != null ? Number(sh.numberOfShares) : null,
                  percentage: sh.percentage != null ? Number(sh.percentage) : null,
                })),
              }))
            : null,
      };

  const transferability = fund.transferability || null;
  const isCrypto = fund.subscriptionOptions?.subscriptionMethod === 'Crypto';
  const subscriptionOptions = isCrypto
    ? {
        subscriptionMethod: 'Crypto',
        wireInformation: null,
      }
    : fund.subscriptionOptions;

  // Master-Feeder: blank out master-level fields that move to per-feeder; rebuild eligibility
  // with master-level fields only (per-feeder eligibility lives on each feeder body).
  const finalOfferingDocuments = masterFeeder ? null : offeringDocuments;
  const finalSubscriptionDocuments = masterFeeder ? null : subscriptionDocuments;
  const finalRiskDisclosureDocuments = masterFeeder ? null : riskDisclosureDocuments;
  const finalPreviousYearsAudits = masterFeeder ? [] : previousYearsAudits;
  // Master-Feeder share classes live on each feeder; SMA funds must not define share classes.
  const finalSharesStructure =
    masterFeeder || managedAccountSma ? { shares: null } : sharesStructure;
  const finalSubscriptionOptions = masterFeeder ? null : subscriptionOptions;

  const finalInvestorEligibility = masterFeeder
    ? {
        filter: investorEligibility.filter,
        allowed: [] as string[],
        notAllowed: investorEligibility.notAllowed,
        partiallyRestricted: [] as typeof investorEligibility.partiallyRestricted,
        unassigned: isStepForms ? (fund as StepForms).unassignedCountries : null,
      }
    : investorEligibility;

  return {
    name,
    fundStructure,
    feeders: feedersPayload,
    investmentDescription,
    participants,
    returnProfile,
    feesData,
    minimumInvestment,
    redemption,
    duration,
    distributionPeriod,
    reportingPeriod,
    investorEligibility: finalInvestorEligibility,
    countrySpecificExemptions,
    offeringDocuments: finalOfferingDocuments,
    subscriptionDocuments: finalSubscriptionDocuments,
    riskDisclosureDocuments: finalRiskDisclosureDocuments,
    marketingDocuments,
    previousYearsAudits: finalPreviousYearsAudits,
    coverImageId,
    cardImageId,
    logoId,
    investmentAvailabilityStatus,
    transferability,
    sharesStructure: finalSharesStructure,
    subscriptionOptions: finalSubscriptionOptions,
    accountingType,
    investmentCompaniesActStatus,
    howFundWorks: isStepForms ? (fund as StepForms).howFundWorks : (fund as Fund).howFundWorks,
    fundBenefits: isStepForms ? (fund as StepForms).fundBenefits : (fund as Fund).fundBenefits,
    fundPresentation: withDefaultPresentationType(
      isStepForms ? (fund as StepForms).fundPresentation : (fund as Fund).fundPresentation,
    ),
    fundTargetInvestor: isStepForms
      ? (fund as StepForms).fundTargetInvestor
      : (fund as Fund).fundTargetInvestor,
    managerAlignment: isStepForms
      ? (fund as StepForms).managerAlignment
      : (fund as Fund).managerAlignment,
    faq: isStepForms ? (fund as StepForms).faq : (fund as Fund).faq,
  };
}

type NavData = {
  inceptionISO: string | null;
  preferredDataSource: 'file' | 'form' | 'merge' | null;
  uploadedFile: File | null;
  formValues: Record<string, number | undefined>;
  fileValues?: Record<string, number>;
};

function toISODateString(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  } catch {
    return null;
  }
}

function calculateFundEndDate(
  startDate: string | null | undefined,
  durationInYears: number | null | undefined,
  isOpenEnded: boolean | undefined,
): string | null {
  if (isOpenEnded || !startDate || !durationInYears) return null;
  try {
    const start = new Date(startDate);
    if (isNaN(start.getTime())) return null;
    const endYear = start.getFullYear() + durationInYears;
    return `${endYear}-12-31`;
  } catch {
    return null;
  }
}

type FundDuration = {
  startDate?: string | null;
  durationInYears?: number | null;
  isOpenEnded?: boolean;
};

export function mapNavDataToPayload(navData: NavData, fundDuration?: FundDuration) {
  const preferred = navData.preferredDataSource || 'merge';
  const formMap = (navData.formValues || {}) as Record<string, number | undefined>;
  const fileMap = (navData.fileValues || {}) as Record<string, number>;

  let merged: Record<string, number> = {};
  if (preferred === 'file') {
    merged = { ...fileMap };
  } else if (preferred === 'merge') {
    const unionKeys = new Set<string>([
      ...Object.keys(formMap || {}),
      ...Object.keys(fileMap || {}),
    ]);
    merged = {};
    unionKeys.forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(formMap, k)) {
        if (formMap[k] !== undefined) merged[k] = Number(formMap[k]);
      } else if (fileMap[k] !== undefined) {
        merged[k] = Number(fileMap[k]);
      }
    });
  } else {
    merged = Object.fromEntries(
      Object.entries(formMap).filter(([, v]) => v !== undefined),
    ) as Record<string, number>;
  }

  const inceptionISO = toISODateString(fundDuration?.startDate);
  const endISO = calculateFundEndDate(
    fundDuration?.startDate,
    fundDuration?.durationInYears,
    fundDuration?.isOpenEnded,
  );

  const entries = Object.keys(merged)
    .sort()
    .filter((date) => {
      if (inceptionISO && date < inceptionISO) return false;
      if (endISO && date > endISO) return false;
      return true;
    })
    .map((date) => ({
      date,
      value: Number(merged[date]),
    }));
  return {
    mode: 'replace' as const,
    entries,
  };
}
